/**
 * The operations portal's start-up read: everything the frame around a screen
 * asks for on load, in ONE request.
 *
 * Every Internal screen used to open with up to three calls of the frame's on
 * top of its own - the platform clock (`/internal/time`, every dated screen's
 * default day), the ordering switches (`/internal/ordering`, the Admin
 * dashboard's closed banner) and the Support badge (`/internal/support/counts`,
 * the rail). Each is cheap; what is not cheap is a call landing on a cold
 * Worker isolate, which waits 0.5-1.2 s before the handler runs, and the more
 * calls a first screen fires the likelier one of them does (demo, 24 Oct 2026:
 * ~8% of calls, ~36% of loads).
 *
 * The answer is exactly the three answers, under the three existing schemas, so
 * the portal writes each into the cache entry its own endpoint fills and every
 * reader stays as it is. The three endpoints stay: the polls and later
 * refetches still go to them.
 */
import { z } from 'zod';
import { defineEndpoint } from '../http';
import { PlatformTime } from './orders';
import { OrderingModes } from './settings';
import { SupportCounts } from './support';

export const getShell = defineEndpoint({
  method: 'GET',
  path: '/internal/shell',
  mount: 'internal',
  summary:
    'The portal frame on load: platform clock, ordering switches and Support counts (OPS-001, OPS-025)',
  response: z.object({
    /** `GET /internal/time`'s answer. */
    time: PlatformTime,
    /** `GET /internal/ordering`'s answer. */
    ordering: OrderingModes,
    /** `GET /internal/support/counts`'s answer. */
    supportCounts: SupportCounts,
  }),
});

export const shellEndpoints = { getShell } as const;
